<template>
    <rkm-list-group>
        <div
            v-for="(item, i) in sortedPrescricaoInterna"
            v-bind:key="i"
            :class="i === data.form.prescricoesInterna.concat(data.model.prescricoesInterna).length - 1 ? '' : 'mb-1'"
        >
            <rkm-list-group-item class="rkm-list-group-item">
                <rkm-row class="align-items-center">
                    <rkm-col cols="12">
                        <rkm-row>
                            <rkm-col cols="auto">
                                <div v-if="Number.isInteger(item.id)">
                                    <rkm-icon
                                        :id="`pronto-atendimento-prescricao-interna-info-${item.id}`"
                                        icon="info-circle"
                                        class="mr-2"
                                    />

                                    {{ item.dataHora }}

                                    <rkm-popover
                                        :target="`pronto-atendimento-prescricao-interna-info-${item.id}`"
                                        triggers="hover"
                                    >
                                        <div>{{ item.centroCusto.setor.nome }}</div>
                                        <div><rkm-icon icon="user" /> {{ item.lotacao.profissional.nome }} - {{ item.lotacao.ocupacao.nome }}</div>
                                        <div>Class. risco: {{ item.classificacaoRisco.nome }}</div>
                                    </rkm-popover>
                                </div>
                            </rkm-col>

                            <rkm-col cols="auto">
                                {{ item.medicamentos.length }} Medicamento(s)
                            </rkm-col>

                            <rkm-col cols="auto">
                                <template v-if="item.doseUnica">
                                    <strong>Dose única</strong>
                                </template>

                                <template v-else>
                                    <strong>Frequência:</strong> {{ item.frequencia }}
                                </template>
                            </rkm-col>
                        </rkm-row>
                    </rkm-col>
                </rkm-row>
            </rkm-list-group-item>

            <rkm-list-group class="rkm-list-group">
                <rkm-list-group-item v-if="item.observacoes">
                    <rkm-area title="Observações da prescrição">
                        <rkm-row>
                            <rkm-col class="pt-2">
                                <div v-if="item.observacoes">
                                    {{ item.observacoes }}
                                </div>
                            </rkm-col>
                        </rkm-row>
                    </rkm-area>
                </rkm-list-group-item>

                <rkm-list-group-item
                    v-for="medicamento in item.medicamentos"
                    v-bind:key="uniqueId(medicamento.id)"
                >
                    <rkm-area title="Medicamento">
                        <rkm-row class="align-items-center">
                            <rkm-col class="flex-grow-1">
                                <div>
                                    <strong>{{ medicamento.medicamento.nome }}</strong> - {{medicamento.quantidade}} {{medicamento.unidadeMedida.sigla}} {{medicamento.formaFarmaceuticaNome || medicamento.medicamento.formaFarmaceutica.nome}}
                                </div>

                                <div v-if="medicamento.administracao">
                                    {{ medicamento.administracao }}
                                </div>
                            </rkm-col>
                        </rkm-row>
                    </rkm-area>

                    <rkm-area title="Frequência">
                        <rkm-row>
                            <rkm-col
                                v-for="(aplicacao, index) in medicamento.aplicacoes"
                                v-bind:key="uniqueId(aplicacao.id)"
                                class="background-aplicacao m-2 p-2 text-center"
                           >
                                <rkm-row>
                                    <rkm-col>
                                        <rkm-form-radio-group
                                            :label="`${index + 1}º Aplicação`"
                                            buttons
                                            v-model="aplicacao.status.id"
                                        >
                                            <rkm-form-radio
                                                color="success"
                                                :outline="aplicacao.disabled ? !(aplicacao.status.id === enums.status.REALIZADO) : true"
                                                :value="enums.status.REALIZADO"
                                                :disabled="aplicacao.disabled"
                                                v-rkm-tooltip.hover
                                                title="Realizado"
                                            >
                                                <rkm-icon icon="check" />
                                            </rkm-form-radio>

                                            <rkm-form-radio
                                                color="primary"
                                                :outline="aplicacao.disabled ? !(aplicacao.status.id === enums.status.SOLICITADO) : true"
                                                :value="enums.status.SOLICITADO"
                                                :disabled="aplicacao.disabled"
                                                v-rkm-tooltip.hover
                                                title="Solicitado"
                                            >
                                                <rkm-icon icon="hourglass" />
                                            </rkm-form-radio>

                                            <rkm-form-radio
                                                @click="clear(aplicacao)"
                                                color="danger"
                                                :outline="aplicacao.disabled ? !(aplicacao.status.id === enums.status.NAO_REALIZADO) : true"
                                                :value="enums.status.NAO_REALIZADO"
                                                :disabled="aplicacao.disabled"
                                                v-rkm-tooltip.hover
                                                title="Não realizado"
                                            >
                                                <rkm-icon icon="times" />
                                            </rkm-form-radio>
                                        </rkm-form-radio-group>
                                    </rkm-col>
                                </rkm-row>

                                <rkm-row>
                                    <rkm-col>
                                        <rkm-form-time-picker
                                            :label="`Horário da ${index + 1}º Aplicação`"
                                            v-model="aplicacao.horarioAplicacao"
                                            :disabled="aplicacao.disabled || aplicacao.status.id === enums.status.NAO_REALIZADO"
                                            :validations="{ required: aplicacao.status.id === enums.status.REALIZADO }"
                                        />
                                    </rkm-col>
                                </rkm-row>

                                <template v-if="data.form.prescricaoInternaViasAdministracao">
                                    <rkm-row>
                                        <rkm-col>
                                            <rkm-form-select
                                                :disabled="aplicacao.disabled || aplicacao.status.id === enums.status.NAO_REALIZADO"
                                                :label="`Local anatômico da ${index + 1}º Aplicação`"
                                                :options="data.form.prescricaoInternaLocaisAnatomico"
                                                :validations="{ required: aplicacao.status.id === enums.status.REALIZADO }"
                                                @change="handleLocalAnatomicoChange(aplicacao)"
                                                show-default-option
                                                textField="descricao"
                                                v-model="aplicacao.localAnatomicoId"
                                                valueField="id"
                                            />
                                        </rkm-col>
                                    </rkm-row>

                                    <rkm-row>
                                        <rkm-col>
                                            <rkm-form-select
                                                :disabled="aplicacao.disabled || aplicacao.status.id === enums.status.NAO_REALIZADO || regioesDisponiveis(aplicacao).length === 0"
                                                :label="`Sub-região da ${index + 1}º Aplicação`"
                                                :options="regioesDisponiveis(aplicacao)"
                                                show-default-option
                                                textField="descricao"
                                                v-model="aplicacao.localAnatomicoRegiaoId"
                                                valueField="id"
                                            />
                                        </rkm-col>
                                    </rkm-row>

                                    <rkm-row>
                                        <rkm-col>
                                            <rkm-form-select
                                                :disabled="aplicacao.disabled || aplicacao.status.id === enums.status.NAO_REALIZADO"
                                                :label="`Via de Administração da ${index + 1}º Aplicação`"
                                                :options="data.form.prescricaoInternaViasAdministracao"
                                                :validations="{ required: aplicacao.status.id === enums.status.REALIZADO }"
                                                show-default-option
                                                textField="nome"
                                                v-model="aplicacao.viaAdministracaoId"
                                                valueField="id"
                                            />
                                        </rkm-col>
                                    </rkm-row>
                                </template>

                                <rkm-row v-else>
                                    <rkm-col>
                                        <rkm-form-select
                                            :disabled="aplicacao.disabled || aplicacao.status.id === enums.status.NAO_REALIZADO"
                                            :label="`Local da ${index + 1}º Aplicação`"
                                            :options="data.form.prescricaoInternaLocaisAplicacao"
                                            :validations="{ required: aplicacao.status.id === enums.status.REALIZADO }"
                                            show-default-option
                                            textField="descricao"
                                            v-model="aplicacao.localAplicacaoId"
                                            valueField="id"
                                        />
                                    </rkm-col>
                                </rkm-row>

                                <rkm-row v-if="aplicacao.disabled">
                                    <rkm-col>
                                        <span>
                                            <strong>Profissional</strong>
                                        </span>

                                        <div>
                                            <span>
                                                {{ aplicacao.profissional.nome }}
                                            </span>
                                        </div>
                                    </rkm-col>
                                </rkm-row>

                                <rkm-row v-if="aplicacao.disabled && aplicacao.status.id === enums.status.REALIZADO && data.form.exigirDuplaChecagemMedicamento">
                                    <rkm-col>
                                        <template v-if="aplicacao.duplaChecagemEm">
                                            <span>
                                                <strong>Dupla checagem</strong>
                                            </span>

                                            <div>
                                                {{ aplicacao.duplaChecagemProfissional.nome }}
                                            </div>

                                            <div>
                                                {{ aplicacao.duplaChecagemEm }}
                                            </div>
                                        </template>

                                        <template v-else>
                                            <span>
                                                <strong>Dupla checagem</strong>
                                            </span>

                                            <div v-if="lotacao.id === aplicacao.profissional.lotacaoId">
                                                Aguardando confirmação de outro profissional
                                            </div>

                                            <div v-else>
                                                <rkm-button
                                                    :outline="!aplicacao.duplaChecagemConfirmar"
                                                    @click="toggleDuplaChecagemConfirmar(aplicacao)"
                                                    color="success"
                                                    small
                                                    title="Confirmar dupla checagem"
                                                    v-rkm-tooltip.hover
                                                >
                                                    <rkm-icon icon="check" />
                                                </rkm-button>
                                            </div>
                                        </template>
                                    </rkm-col>
                                </rkm-row>
                            </rkm-col>
                        </rkm-row>
                    </rkm-area>
                </rkm-list-group-item>
            </rkm-list-group>
        </div>
    </rkm-list-group>
</template>

<script>
// Enums.
import medidaTempo from '@/enums/cadastro/medidaTempo'
import status from '@/enums/pronto-atendimento/statusExame'
import unidadeMedida from '@/enums/cadastro/unidadeMedida'
import moment from 'moment';

// Vuex.
import { mapGetters } from 'vuex'

export default {
    data: () => ({
        enums: {
            medidaTempo,
            status,
            unidadeMedida,
        },
    }),
    computed: {
        ...mapGetters({ lotacao: 'getLotacao' }),
        sortedPrescricaoInterna: function () {

            let concatPrescricaoInterna = this.data.form.prescricoesInterna.concat(this.data.model.prescricoesInterna)

            return concatPrescricaoInterna.sort((a, b) => {
                return moment(a.dataHora, 'DD/MM/YYYY HH:mm:ss') - moment(b.dataHora, 'DD/MM/YYYY HH:mm:ss')
            })
        },
    },
    methods: {
        clear(aplicacao) {

            aplicacao.horarioAplicacao = null

            if (this.data.form.prescricaoInternaViasAdministracao) {

                aplicacao.viaAdministracaoId = null
                aplicacao.localAnatomicoId = null
                aplicacao.localAnatomicoRegiaoId = null
            } else {

                aplicacao.localAplicacaoId = null
            }
        },
        handleLocalAnatomicoChange(aplicacao) {

            aplicacao.localAnatomicoRegiaoId = null
        },
        regioesDisponiveis(aplicacao) {

            return (this.data.form.prescricaoInternaLocaisAnatomicoRegiao || [])
                .filter(regiao => regiao.localAnatomicoId === aplicacao.localAnatomicoId)
        },
        toggleDuplaChecagemConfirmar(aplicacao) {

            this.$set(aplicacao, 'duplaChecagemConfirmar', !aplicacao.duplaChecagemConfirmar)
        },
    },
    props: {
        data: { type: Object, required: true },
    },
}
</script>

<style>
.background-aplicacao {
    background: #fbfbfb;
    border: 1px solid #ebebeb;
    min-width: 200px;
    max-width: 200px;
}
</style>